/**
 * Thin, typed wrapper around the Google Ads tag (gtag.js / `window.gtag`), plus
 * its consent-gated loader.
 *
 * The Google Ads tag sets advertising cookies (`_gcl_*`) and sends hits to
 * Google, which Quebec Law 25 (Bill 64) and PIPEDA treat as non-essential
 * tracking that requires opt-in BEFORE it runs. So, exactly like the Meta Pixel,
 * the base tag is NOT loaded eagerly from index.html. Nothing about Google Ads
 * touches the visitor until `initGoogleAds()` runs, and that only happens after
 * the visitor accepts in CookieConsent (see src/main.tsx). Default state = tag
 * dark. (Loading it from index.html would re-open the QA F11 gap.)
 *
 * Every helper double-checks consent and no-ops safely when gtag is absent (no
 * consent yet, SSR, ad-blockers) so callers never need to guard.
 *
 * Cross-domain: a Google Ads click lands on one cipherandrow.com property and
 * the sign-up completes on another (ad landing -> .../signup). `linker.domains`
 * in the config call below decorates outbound links/redirects with the `_gl`
 * parameter so the gclid (ad-click id) carries across every hop and the
 * conversion is attributed to the click. gtag already auto-links same-root
 * subdomains via the shared root-domain cookie, so for subdomain hops this is
 * belt-and-suspenders; it's the actual fix if any property ever moves to a
 * different root domain. For it to work both ways, the tag on the *landing*
 * page must list the same domains in its own linker config.
 */

import { hasAnalyticsConsent } from "@/lib/consent";

// Google Ads conversion account ID. Single place the tag is configured.
const GOOGLE_ADS_ID = "AW-18320958307";

// Domains in the ad-click -> sign-up journey, decorated with the `_gl` linker
// param so the gclid survives each hop. Keep this trimmed to the hosts that are
// actually live: go.cipherandrow.com is the ad-landing domain (per Signup.tsx),
// www/root cipherandrow.com is where sign-up completes; app.cipherandrow.com is
// the documented dashboard host (docs only today) — included for forward-compat.
const CROSS_DOMAIN_LINKER_DOMAINS = [
  "cipherandrow.com",
  "www.cipherandrow.com",
  "go.cipherandrow.com",
  "app.cipherandrow.com",
];

// Google Ads conversion label for the "Sign-up 2" conversion action. This is
// the opaque, Google-generated token from the action's event snippet (Google
// Ads → Goals → Conversions → the sign-up action → "Tag setup" → Use Google
// tag), NOT a human-readable name. Combined with GOOGLE_ADS_ID it forms the
// `send_to` value `AW-18320958307/XgTGCNuVitEcEOPGjqBE`. If the conversion
// action is recreated in Google Ads, copy the new token from its snippet here.
const SIGNUP_CONVERSION_LABEL = "XgTGCNuVitEcEOPGjqBE";

// Conversion value + currency, matching the Google Ads event snippet for the
// sign-up action. Google records a fixed 1.0 USD per sign-up so the action has
// a comparable value in reporting/bidding; keep these in sync with the snippet.
const SIGNUP_CONVERSION_VALUE = 1.0;
const SIGNUP_CONVERSION_CURRENCY = "USD";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

// Guards against a double load if initGoogleAds() is called more than once
// (idle callback + consent event in the same session). Mirrors meta-pixel.ts.
let initialized = false;

/**
 * Load gtag.js and configure the Google Ads account.
 *
 * No-ops unless the visitor has accepted analytics consent, so this is safe to
 * call eagerly — the consent check is the gate. Mirrors initMetaPixel().
 */
export function initGoogleAds(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  if (initialized || window.gtag) return;
  initialized = true;

  // Inject the gtag.js loader. Because this runs post-consent, no Google Ads
  // cookie/hit precedes opt-in.
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  (document.head ?? document.body ?? document.documentElement).appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Canonical gtag shim (verbatim from Google's snippet): queues each command's
  // `arguments` object onto dataLayer for gtag.js to drain once it loads. Kept
  // byte-identical to what Google documents so command tuples are shaped exactly
  // as gtag.js expects.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as Gtag;

  window.gtag("js", new Date());
  // `config` carries the cross-domain linker so gclid attribution survives the
  // ad-domain -> sign-up-domain hop (see CROSS_DOMAIN_LINKER_DOMAINS).
  window.gtag("config", GOOGLE_ADS_ID, {
    linker: { domains: CROSS_DOMAIN_LINKER_DOMAINS },
  });
}

/**
 * Fire the Google Ads sign-up conversion.
 *
 * Call this at real account-creation success (not a button click). No-ops
 * without analytics consent. Reports to
 * `AW-18320958307/XgTGCNuVitEcEOPGjqBE` (GOOGLE_ADS_ID /
 * SIGNUP_CONVERSION_LABEL) with a 1.0 USD value, mirroring the Google Ads
 * event snippet for the "Sign-up 2" conversion action.
 */
export function trackGoogleAdsConversion(): void {
  if (!hasAnalyticsConsent()) return;
  window.gtag?.("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${SIGNUP_CONVERSION_LABEL}`,
    value: SIGNUP_CONVERSION_VALUE,
    currency: SIGNUP_CONVERSION_CURRENCY,
  });
}
